import { Box } from '@material-ui/core';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import { connect, useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { RequestStatus } from '../../types/requestStatus';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { withViewport } from '../../util/contextHelpers';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  AvatarLarge,
  Footer,
  FormattedMessage,
  IconSpinner,
  LayoutSideNavigation,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperSideNav,
  LayoutWrapperTopbar,
  ListingCard,
  NamedLink,
  Page,
  TypographyWrapper,
} from '../../components';
import { useShopConfig } from '../../hooks/shopConfig';
import { defaultTreetStyles } from '../../shopConfig/config';
import { NotFoundPage, TopbarContainer } from '..';
import { queryUserListings } from './ProfilePage.duck';
import css from './ProfilePage.module.css';
import { pluralize } from '../../util/strings';

const Loading = () => (
  <Box display="flex" justifyContent="center" pt="40px" width="100%">
    <IconSpinner />
  </Box>
);

const Heading = (props) => {
  const { displayName } = props;
  const {
    listingIds,
    openListingsPagination,
    soldListingsPagination,
    queryUserListingsStatus,
    queryListingsError,
    userShowError,
  } = useSelector((state) => state.ProfilePage);

  const isInitialLoad =
    listingIds.length === 0 &&
    (queryUserListingsStatus === RequestStatus.Ready ||
      queryUserListingsStatus === RequestStatus.Pending);
  if (isInitialLoad) return null;

  const error = queryListingsError || userShowError;
  if (error) {
    return (
      <h2 className={css.error}>
        <TypographyWrapper
          variant="body1"
          typographyOverrides={{ style: { color: defaultTreetStyles.red80 } }}
        >
          <FormattedMessage id="ProfilePage.loadingDataFailed" />
        </TypographyWrapper>
      </h2>
    );
  }

  const listingsCount =
    !isEmpty(openListingsPagination) || !isEmpty(soldListingsPagination)
      ? (openListingsPagination.totalItems || 0) + (soldListingsPagination.totalItems || 0)
      : null;
  if (listingsCount === 0) {
    return (
      <h1 className={css.listingsTitle}>
        <TypographyWrapper variant="h2">
          <FormattedMessage id="ProfilePage.noResults" />
        </TypographyWrapper>
      </h1>
    );
  }

  return (
    <h1 className={css.listingsTitle}>
      <TypographyWrapper variant="h2">
        <TypographyWrapper variant="h2" typographyOverrides={{ display: 'inline' }} isDynamicValue>
          {displayName}
        </TypographyWrapper>
        ’s items{' '}
        <TypographyWrapper variant="h2" typographyOverrides={{ display: 'inline' }} isDynamicValue>
          ({listingsCount})
        </TypographyWrapper>
      </TypographyWrapper>
    </h1>
  );
};

const UserListings = (props) => {
  const { listings } = props;

  return (
    <ul className={css.listings}>
      {listings.map((l) => (
        <li className={css.listing} key={l.id.uuid}>
          <ListingCard listing={l} referrerLocation="Profile Page" />
        </li>
      ))}
    </ul>
  );
};

export const ProfilePageComponent = (props) => {
  const { scrollingDisabled, currentUser, user, userShowError, listings, intl } = props;

  const dispatch = useDispatch();
  const { treetShopName } = useShopConfig();

  const { openListingsPagination, soldListingsPagination, queryUserListingsStatus } = useSelector(
    (state) => state.ProfilePage
  );
  const profileUser = ensureUser(user);

  useEffect(() => {
    if (profileUser.id) {
      dispatch(queryUserListings({ userId: profileUser.id }));
    }
  }, [profileUser.id]);

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage />;
  }

  const hasMoreToFetch =
    !openListingsPagination ||
    !soldListingsPagination ||
    openListingsPagination.page < openListingsPagination.totalPages ||
    soldListingsPagination.page < soldListingsPagination.totalPages;
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid;
  const { bio, displayName } = profileUser.attributes.profile;
  const hasBio = !!bio;

  const schemaTitle = intl.formatMessage(
    { id: 'ProfilePage.schemaTitle' },
    {
      name: displayName,
      siteTitle: treetShopName,
    }
  );

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  const editLinkMobile = !!isCurrentUser && (
    <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
      <TypographyWrapper variant="body1">
        <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
      </TypographyWrapper>
    </NamedLink>
  );
  const editLinkDesktop = !!isCurrentUser && (
    <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
      <TypographyWrapper variant="body1">
        <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
      </TypographyWrapper>
    </NamedLink>
  );

  const asideContent = (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <h1 className={css.mobileHeading}>
        {displayName ? (
          <TypographyWrapper variant="h2" isDynamicValue>
            <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
          </TypographyWrapper>
        ) : null}
      </h1>
      {editLinkMobile}
      {editLinkDesktop}
    </div>
  );

  const userImage = user?.profileImage?.attributes?.variants?.['square-small']?.url || null;
  const areListingsLoaded =
    queryUserListingsStatus === RequestStatus.Success ||
    queryUserListingsStatus === RequestStatus.Error;

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: displayName,
          ...(userImage ? { image: userImage } : {}),
          description:
            bio || `Selling ${pluralize('item', listings.length, true)} on ${treetShopName}.`,
        },
      }}
      shouldNotIndex
      contentType="profile"
    >
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="ProfilePage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperSideNav className={css.aside}>{asideContent}</LayoutWrapperSideNav>
        <LayoutWrapperMain>
          {!!displayName && (
            <>
              <h1 className={css.desktopHeading}>
                <TypographyWrapper variant="h2">
                  Hello, I’m{' '}
                  <TypographyWrapper
                    variant="h2"
                    typographyOverrides={{ display: 'inline' }}
                    isDynamicValue
                  >
                    {displayName}
                  </TypographyWrapper>
                </TypographyWrapper>
              </h1>
              {hasBio && (
                <p className={css.bio}>
                  <TypographyWrapper variant="body1" isDynamicValue>
                    {bio}
                  </TypographyWrapper>
                </p>
              )}
            </>
          )}
          <div className={listingsContainerClasses}>
            <Heading displayName={displayName} />
            <InfiniteScroll
              dataLength={listings.length}
              next={() => {
                if (queryUserListingsStatus !== RequestStatus.Pending && profileUser.id) {
                  dispatch(queryUserListings({ userId: profileUser.id }));
                }
              }}
              hasMore={hasMoreToFetch}
              loader={<Loading />}
            >
              <UserListings listings={listings} />
            </InfiniteScroll>
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>{areListingsLoaded && <Footer />}</LayoutWrapperFooter>
      </LayoutSideNavigation>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
};

const { bool, arrayOf } = PropTypes;

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,

  intl: intlShape.isRequired,
};

const mapStateToProps = (state) => {
  const { currentUser } = state.user;
  const { userId, userShowError, listingIds } = state.ProfilePage;

  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const listings = getListingsById(state, listingIds);

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    listings,
  };
};

const ProfilePage = compose(
  connect(mapStateToProps),
  withViewport,
  injectIntl
)(ProfilePageComponent);

export default ProfilePage;
